import { createSlice } from "@reduxjs/toolkit";
import {
  CaptainSummary,
  CaptainTeam,
  PayoutDetails,
  PendingBusiness,
  Settlement,
  SettlementRequest,
} from "../../interfaces/OnboardingObject";
import { resetAllState } from "./resetAllState";
import {
  fetchCaptainSummary,
  fetchPendingBusinesses,
  createPendingBusiness,
  regeneratePaymentLink,
  createCaptain,
  fetchMyTeam,
  settleCaptain,
  recordSettlement,
  fetchSettlements,
  fetchMyPendingRequest,
  fetchTeamRequests,
  requestSettlement,
  cancelSettlementRequest,
  fetchLastPayout,
} from "../../services/thunks/OnboardingThunk";

/**
 * Gate state for the Onboarding Program tab:
 * - "unknown":    summary not fetched yet this session → fetch on tab open.
 * - "notCaptain": summary returned CAPTAIN_NOT_FOUND → "not enrolled" view.
 * - "loggedOut":  summary call failed with no/invalid session (UNAUTHENTICATED
 *                 or a bare HTTP 401) — a guest or expired session can never
 *                 resolve to "captain", so this is terminal like "notCaptain",
 *                 just kept distinct so it doesn't drive "not enrolled" copy.
 * - "captain":    summary loaded → full program view.
 * Cached per session by design — a user enrolled by admin mid-session sees the
 * captain view after reload (empty-state copy must not imply real-time status).
 */
type CaptainStatus = "unknown" | "notCaptain" | "loggedOut" | "captain";

interface OnboardingState {
  captainStatus: CaptainStatus;
  summary: CaptainSummary | null;
  /** All of the captain's submissions (unfiltered); chips filter client-side. */
  pendingBusinesses: PendingBusiness[];
  /**
   * Lead's team roster. Read once per fetch and intentionally NOT refreshed
   * live (admin approvals mid-session won't update it). Appending a new
   * captain does not stale it — a new captain has ₹0 earned/unsettled.
   */
  team: CaptainTeam | null;
  /** The caller's own payout history, newest first. */
  settlements: Settlement[];
  /** The caller's own PENDING settlement request. undefined = not fetched yet
   *  (the Settlements tab must not offer "Request" until it knows); null =
   *  fetched, none open. */
  pendingRequest: SettlementRequest | null | undefined;
  /** PENDING requests the caller has to pay (a lead's team). Re-read after
   *  every settleCaptain — the server decides whether a payout closes one. */
  teamRequests: SettlementRequest[];
  /** Payout details from the caller's last settled request (prefill only).
   *  undefined = not fetched yet. In memory only; wiped on logout. */
  lastPayout: PayoutDetails | null | undefined;
  loadingMap: Record<string, string | null>;
  errorMap: Record<string, string | null>;
  successMap: Record<string, boolean>;
}

const initialState: OnboardingState = {
  captainStatus: "unknown",
  summary: null,
  pendingBusinesses: [],
  team: null,
  settlements: [],
  pendingRequest: undefined,
  teamRequests: [],
  lastPayout: undefined,
  loadingMap: {},
  errorMap: {},
  successMap: {},
};

/** Replace the row with the same pendingId, or prepend if it's new — the
 *  idempotent server create can return an EXISTING row (double-tap, link
 *  regeneration), which must never duplicate a card.
 *  In-place mutation by design: only ever called on Immer drafts inside this
 *  slice's reducers (state.pendingBusinesses is always initialized to []). */
const upsertPendingBusiness = (
  list: PendingBusiness[],
  row: PendingBusiness
): void => {
  const existingIdx = list.findIndex((pb) => pb.pendingId === row.pendingId);
  if (existingIdx >= 0) {
    list[existingIdx] = row;
  } else {
    list.unshift(row);
  }
};

const onboardingSlice = createSlice({
  name: "onboarding",
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    // fetchCaptainSummary (gate call)
    builder
      .addCase(fetchCaptainSummary.pending, (state) => {
        state.loadingMap.fetchCaptainSummary = "Loading your dashboard...";
        state.errorMap.fetchCaptainSummary = null;
        state.successMap.fetchCaptainSummary = false;
      })
      .addCase(fetchCaptainSummary.fulfilled, (state, action) => {
        state.loadingMap.fetchCaptainSummary = null;
        state.successMap.fetchCaptainSummary = true;
        state.captainStatus = "captain";
        state.summary = action.payload;
      })
      .addCase(fetchCaptainSummary.rejected, (state, action) => {
        state.loadingMap.fetchCaptainSummary = null;
        // A failed BACKGROUND refresh (e.g. after draft submit) must not tear
        // down an already-confirmed captain view — keep the stale summary.
        // Session-cached semantics already accept staleness until reload.
        if (state.captainStatus === "captain") return;
        // Thunk rejects with the RAW errorCode: CAPTAIN_NOT_FOUND is the
        // "not enrolled" state, not an error to surface.
        if (action.payload === "CAPTAIN_NOT_FOUND") {
          state.captainStatus = "notCaptain";
          state.summary = null;
        } else if (
          action.payload === "UNAUTHENTICATED" ||
          action.payload === "HTTP_401"
        ) {
          // No/invalid session. Every call site gates its fetch on
          // `captainStatus === "unknown"`, so leaving this at "unknown"
          // made the fetch retry on every subsequent render forever — this
          // is what hammered the API with 401s for every logged-out visitor
          // to a business page. Resolve it out of "unknown" the same way
          // CAPTAIN_NOT_FOUND does, just under its own status (see the
          // CaptainStatus doc comment for why not "notCaptain").
          state.captainStatus = "loggedOut";
          state.summary = null;
        } else {
          state.errorMap.fetchCaptainSummary = action.payload as string;
        }
      });

    // fetchPendingBusinesses
    builder
      .addCase(fetchPendingBusinesses.pending, (state) => {
        state.loadingMap.fetchPendingBusinesses = "Loading your submissions...";
        state.errorMap.fetchPendingBusinesses = null;
        state.successMap.fetchPendingBusinesses = false;
      })
      .addCase(fetchPendingBusinesses.fulfilled, (state, action) => {
        state.loadingMap.fetchPendingBusinesses = null;
        state.successMap.fetchPendingBusinesses = true;
        state.pendingBusinesses = action.payload;
      })
      .addCase(fetchPendingBusinesses.rejected, (state, action) => {
        state.loadingMap.fetchPendingBusinesses = null;
        state.errorMap.fetchPendingBusinesses = action.payload as string;
      });

    // createPendingBusiness
    builder
      .addCase(createPendingBusiness.pending, (state) => {
        state.loadingMap.createPendingBusiness = "Submitting draft business...";
        state.errorMap.createPendingBusiness = null;
        state.successMap.createPendingBusiness = false;
      })
      .addCase(createPendingBusiness.fulfilled, (state, action) => {
        state.loadingMap.createPendingBusiness = null;
        state.successMap.createPendingBusiness = true;
        upsertPendingBusiness(state.pendingBusinesses, action.payload);
      })
      .addCase(createPendingBusiness.rejected, (state, action) => {
        state.loadingMap.createPendingBusiness = null;
        state.errorMap.createPendingBusiness = action.payload as string;
      });

    // regeneratePaymentLink (idempotent create re-call → same row, fresh link)
    // PER-ROW loadingMap keys — a global key would disable the Regenerate
    // button on every UNPAID card while any single row regenerates.
    builder
      .addCase(regeneratePaymentLink.pending, (state, action) => {
        const key = `regeneratePaymentLink_${action.meta.arg.pendingId}`;
        state.loadingMap[key] = "Regenerating payment link...";
        state.errorMap[key] = null;
        state.successMap[key] = false;
      })
      .addCase(regeneratePaymentLink.fulfilled, (state, action) => {
        const key = `regeneratePaymentLink_${action.meta.arg.pendingId}`;
        state.loadingMap[key] = null;
        state.successMap[key] = true;
        upsertPendingBusiness(state.pendingBusinesses, action.payload);
      })
      .addCase(regeneratePaymentLink.rejected, (state, action) => {
        const key = `regeneratePaymentLink_${action.meta.arg.pendingId}`;
        state.loadingMap[key] = null;
        state.errorMap[key] = action.payload as string;
      });

    // createCaptain (lead only)
    builder
      .addCase(createCaptain.pending, (state) => {
        state.loadingMap.createCaptain = "Creating captain...";
        state.errorMap.createCaptain = null;
        state.successMap.createCaptain = false;
      })
      .addCase(createCaptain.fulfilled, (state, action) => {
        state.loadingMap.createCaptain = null;
        state.successMap.createCaptain = true;
        // Append only; team totals stay as-fetched (new captain has ₹0 — see
        // the staleness note on OnboardingState.team). No refetch here.
        if (state.team) {
          state.team.captains.push(action.payload);
        }
      })
      .addCase(createCaptain.rejected, (state, action) => {
        state.loadingMap.createCaptain = null;
        state.errorMap.createCaptain = action.payload as string;
      });

    // settleCaptain (records a payout for one team member)
    // PER-ROW loadingMap key (like regeneratePaymentLink) so settling one
    // captain doesn't disable every other row's Settle button. The component
    // reads this exact key to guard against double-submit.
    builder
      .addCase(settleCaptain.pending, (state, action) => {
        const key = `settleCaptain_${action.meta.arg.captainId}`;
        state.loadingMap[key] = "Recording settlement...";
        state.errorMap[key] = null;
        state.successMap[key] = false;
      })
      .addCase(settleCaptain.fulfilled, (state, action) => {
        const key = `settleCaptain_${action.meta.arg.captainId}`;
        state.loadingMap[key] = null;
        state.successMap[key] = true;
        // Refresh the settled figure (and commissions, in case they moved) on
        // the matching team row from the authoritative response.
        const updated = action.payload;
        if (state.team) {
          const row = state.team.captains.find(
            (c) => c.captainId === updated.captainId
          );
          if (row) {
            row.totalSettled = updated.totalSettled;
            row.totalCaptainCommission = updated.totalCaptainCommission;
            row.totalLeadCommission = updated.totalLeadCommission;
          }
        }
      })
      .addCase(settleCaptain.rejected, (state, action) => {
        const key = `settleCaptain_${action.meta.arg.captainId}`;
        state.loadingMap[key] = null;
        state.errorMap[key] = action.payload as string;
      });

    // recordSettlement (generic payout by payee user id — the Team tab's
    // Settle button). PER-PAYEE loadingMap key, like settleCaptain. The roster
    // row's settled figure moves at once (a stopgap so the row isn't stale while
    // the re-read is in flight); the thunk then re-reads the roster, and
    // fetchMyTeam.fulfilled replaces state.team wholesale — so this increment is
    // overwritten by the server's figure and can never compound.
    builder
      .addCase(recordSettlement.pending, (state, action) => {
        const key = `recordSettlement_${action.meta.arg.payeeUserId}`;
        state.loadingMap[key] = "Recording settlement...";
        state.errorMap[key] = null;
        state.successMap[key] = false;
      })
      .addCase(recordSettlement.fulfilled, (state, action) => {
        const key = `recordSettlement_${action.meta.arg.payeeUserId}`;
        state.loadingMap[key] = null;
        state.successMap[key] = true;
        const row = state.team?.captains.find(
          (c) => c.userId === action.meta.arg.payeeUserId
        );
        if (row) row.totalSettled += action.payload.amount;
      })
      .addCase(recordSettlement.rejected, (state, action) => {
        const key = `recordSettlement_${action.meta.arg.payeeUserId}`;
        state.loadingMap[key] = null;
        state.errorMap[key] = action.payload as string;
      });

    // fetchMyTeam (lead only)
    builder
      .addCase(fetchMyTeam.pending, (state) => {
        state.loadingMap.fetchMyTeam = "Loading your team...";
        state.errorMap.fetchMyTeam = null;
        state.successMap.fetchMyTeam = false;
      })
      .addCase(fetchMyTeam.fulfilled, (state, action) => {
        state.loadingMap.fetchMyTeam = null;
        state.successMap.fetchMyTeam = true;
        state.team = action.payload;
      })
      .addCase(fetchMyTeam.rejected, (state, action) => {
        state.loadingMap.fetchMyTeam = null;
        state.errorMap.fetchMyTeam = action.payload as string;
      });

    // fetchSettlements (own payout history)
    builder
      .addCase(fetchSettlements.pending, (state) => {
        state.loadingMap.fetchSettlements = "Loading settlements...";
        state.errorMap.fetchSettlements = null;
        state.successMap.fetchSettlements = false;
      })
      .addCase(fetchSettlements.fulfilled, (state, action) => {
        state.loadingMap.fetchSettlements = null;
        state.successMap.fetchSettlements = true;
        state.settlements = action.payload;
      })
      .addCase(fetchSettlements.rejected, (state, action) => {
        state.loadingMap.fetchSettlements = null;
        state.errorMap.fetchSettlements = action.payload as string;
      });

    // fetchMyPendingRequest
    builder
      .addCase(fetchMyPendingRequest.pending, (state) => {
        state.loadingMap.fetchMyPendingRequest = "Checking your request...";
        state.errorMap.fetchMyPendingRequest = null;
        state.successMap.fetchMyPendingRequest = false;
      })
      .addCase(fetchMyPendingRequest.fulfilled, (state, action) => {
        state.loadingMap.fetchMyPendingRequest = null;
        state.successMap.fetchMyPendingRequest = true;
        state.pendingRequest = action.payload;
      })
      .addCase(fetchMyPendingRequest.rejected, (state, action) => {
        state.loadingMap.fetchMyPendingRequest = null;
        state.errorMap.fetchMyPendingRequest = action.payload as string;
      });

    // fetchTeamRequests (lead only)
    builder
      .addCase(fetchTeamRequests.pending, (state) => {
        state.loadingMap.fetchTeamRequests = "Loading requests...";
        state.errorMap.fetchTeamRequests = null;
        state.successMap.fetchTeamRequests = false;
      })
      .addCase(fetchTeamRequests.fulfilled, (state, action) => {
        state.loadingMap.fetchTeamRequests = null;
        state.successMap.fetchTeamRequests = true;
        state.teamRequests = action.payload;
      })
      .addCase(fetchTeamRequests.rejected, (state, action) => {
        state.loadingMap.fetchTeamRequests = null;
        state.errorMap.fetchTeamRequests = action.payload as string;
      });

    // fetchLastPayout — best effort: a failure just leaves the form empty.
    builder
      .addCase(fetchLastPayout.pending, (state) => {
        state.loadingMap.fetchLastPayout = "Loading payment details...";
        state.errorMap.fetchLastPayout = null;
      })
      .addCase(fetchLastPayout.fulfilled, (state, action) => {
        state.loadingMap.fetchLastPayout = null;
        state.lastPayout = action.payload;
      })
      .addCase(fetchLastPayout.rejected, (state, action) => {
        state.loadingMap.fetchLastPayout = null;
        state.errorMap.fetchLastPayout = action.payload ?? null;
      });

    // requestSettlement — the component blocks double-submit on this key.
    builder
      .addCase(requestSettlement.pending, (state) => {
        state.loadingMap.requestSettlement = "Sending request...";
        state.errorMap.requestSettlement = null;
        state.successMap.requestSettlement = false;
      })
      .addCase(requestSettlement.fulfilled, (state, action) => {
        state.loadingMap.requestSettlement = null;
        state.successMap.requestSettlement = true;
        state.pendingRequest = action.payload;
        // The details just sent are the best prefill for next time.
        if (action.payload.payout) state.lastPayout = action.payload.payout;
      })
      .addCase(requestSettlement.rejected, (state, action) => {
        state.loadingMap.requestSettlement = null;
        state.errorMap.requestSettlement = action.payload as string;
      });

    // cancelSettlementRequest
    builder
      .addCase(cancelSettlementRequest.pending, (state) => {
        state.loadingMap.cancelSettlementRequest = "Cancelling request...";
        state.errorMap.cancelSettlementRequest = null;
        state.successMap.cancelSettlementRequest = false;
      })
      .addCase(cancelSettlementRequest.fulfilled, (state) => {
        state.loadingMap.cancelSettlementRequest = null;
        state.successMap.cancelSettlementRequest = true;
        state.pendingRequest = null;
      })
      .addCase(cancelSettlementRequest.rejected, (state, action) => {
        state.loadingMap.cancelSettlementRequest = null;
        state.errorMap.cancelSettlementRequest = action.payload as string;
      });

    builder.addCase(resetAllState, () => initialState);
  },
});

export default onboardingSlice.reducer;
